import { consume } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { ProductionSpeedTypeEnum } from '@/api-sdk';
import {
  ConfigurationContextType,
  global,
  GlobalComponentType,
  GlobalConfigurationContext,
} from '@/context';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, skewedLabelStyles } from '@/styles';
import { DateType, Mode, Variant } from '@/types';
import { elephantOrderDateTemplate } from '@/utils';

import '../translation/fg-translation-wrapper';
import './hsp/fg-hsp-label';

@customElement('fg-configuration-date')
export class FgConfigurationDate extends LitElement {
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: GlobalConfigurationContext })
  configurationContext!: ConfigurationContextType;

  render() {
    const { layout, locale } = this.globalContext;
    const { mode, modalInset, configurationData, variant } =
      this.configurationContext;

    if (!configurationData) {
      return null;
    }

    const { priceBreakdown, productionSpeed } = configurationData;

    const productionSpeedType = productionSpeed?.type;

    return html`
      ${(priceBreakdown?.expectedShippingDate && layout === 'wholesale') ||
      (priceBreakdown?.expectedDeliveryDate && layout === 'retail')
        ? html`
            <div class="summary-left">
              ${mode === Mode.DEFAULT
                ? html`
                    <div
                      class="${classMap({
                        'default-variant-container':
                          variant === Variant.DEFAULT,
                        'simple-variant-container': variant === Variant.SIMPLE,
                      })} expected-date-container"
                    >
                      ${elephantOrderDateTemplate(
                        layout === 'wholesale'
                          ? priceBreakdown?.expectedShippingDate
                          : priceBreakdown?.expectedDeliveryDate,
                        locale,
                        layout === 'wholesale'
                          ? DateType.SHIPPING
                          : DateType.DELIVERY,
                        !modalInset ||
                          (variant === Variant.SIMPLE &&
                            layout === 'wholesale'),
                        Boolean(
                          productionSpeedType === ProductionSpeedTypeEnum.hsp,
                        ),
                      )}
                    </div>
                  `
                : nothing}
              ${productionSpeedType &&
              productionSpeedType === ProductionSpeedTypeEnum.hsp &&
              variant === Variant.DEFAULT
                ? html`
                    <fg-translation-wrapper
                      .translationKey=${`content.productionSpeed.hspLabelLarge${layout === 'retail' ? 'Retail' : 'Wholesale'}`}
                    >
                      <span slot="inner-html">
                        <fg-hsp-label
                          .layout=${layout}
                          onlySkewedLabel
                          hasLargeOnlySkewedLabel
                        ></fg-hsp-label>
                      </span>
                    </fg-translation-wrapper>
                  `
                : nothing}
            </div>
          `
        : nothing}
    `;
  }
  static styles = [
    baseStyles,
    skewedLabelStyles,
    css`
      .summary-left {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .info-delivery-date {
        color: var(--fg-primary, var(--fg-fallback-primary));
        position: relative;
        top: 2px;
      }

      .shipping-date {
        white-space: nowrap;
        width: 100%;

        & date {
          align-items: center;
          gap: 4px;
          font-weight: 700;
        }

        & .info-shipping-date {
          color: var(--fg-secondary, var(--fg-fallback-secondary));
          position: relative;
          top: 2px;
        }

        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex-direction: column;
            align-items: flex-start;
            gap: 0;
          }
        }

        @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
          && {
            flex-direction: row;
            gap: 4px;
          }
        }
      }

      @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
        .simple-variant-container .expected-delivery-content {
          flex-direction: column;
          display: flex;
        }

        .default-variant-container .expected-delivery-content,
        .default-variant-container .expected-shipping-content {
          width: 100%;
          justify-content: space-between;
          align-items: center;
          display: flex;
        }
      }

      .expected-delivery-content {
        gap: 4px;
      }

      .expected-delivery-date {
        display: flex;
        gap: 4px;
      }

      .expected-delivery-container {
        display: inline-flex;
        gap: 4px;
      }

      .expected-shipping-content {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
          && {
            display: block;
          }
        }
      }

      .expected-date-container {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;
        position: relative;
        margin-bottom: 16px;
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex-direction: column;
          }
        }
      }

      .expected-date-container {
        margin-bottom: 0;
      }

      :host([part='retail']) .expected-date-container {
        width: auto;

        @media screen and (min-width: ${BREAKPOINT_LARGE_PX}) {
          && {
            width: 100%;
          }
        }

        & .shipping-date > div span {
          width: auto;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_SMALL_PX - 1}px) {
        .row div:not(.group):not(.expected-date-container):not(.price-details) {
          display: flex;
          justify-content: space-between;
        }

        .row div.expected-date-container {
          display: flex;
          flex-direction: column;
        }
      }
    `,
  ];
}
